import type { DynamicStructure } from "../types";

type RelationScope = Record<string, unknown> & { relations?: unknown };

/**
 * Achata `scope.relations` em `scope[contentTypeSlug]`, devolvendo o primeiro
 * item de cada relação. Retorna a mesma referência quando não há nada a achatar
 * (mantém a idempotência esperada pelos consumers).
 */
function flattenScopeRelations<T extends RelationScope | null | undefined>(
  scope: T,
): T {
  if (!scope || typeof scope !== "object" || Array.isArray(scope)) {
    return scope;
  }

  const { relations } = scope;
  if (!relations || typeof relations !== "object") {
    return scope;
  }

  const transformed: RelationScope = { ...scope };

  const relationEntries = Object.entries(
    relations as Record<string, any>,
  ).sort(([keyA], [keyB]) => Number(keyB.includes("::")) - Number(keyA.includes("::")));

  relationEntries.forEach(([, relation]) => {
    if (
      relation?.contentTypeSlug &&
      relation.items &&
      relation.items.length > 0
    ) {
      transformed[relation.contentTypeSlug] = relation.items[0];
    }
  });

  delete transformed.relations;

  return transformed as T;
}

/**
 * Achata as relações disponíveis na estrutura de interpolação para
 * `<scope>[contentTypeSlug]`, permitindo que tokens como `{{item.modelo.nome|text}}`
 * (página de detalhe) e `{{section.modelo.nome|text}}` (card de listagem)
 * resolvam o primeiro item da relação sem percorrer o objeto `relations`.
 *
 * - `item.relations` → `item[contentTypeSlug]`
 * - `section.relations` → `section[contentTypeSlug]` (apenas quando `section`
 *   é um objeto único, i.e. o registro do card durante a interpolação per-item)
 *
 * Idempotente: se não há relações a achatar, retorna o input intocado.
 */
export function transformRelationsToFlat(
  dynamicStructure: DynamicStructure,
): DynamicStructure {
  const flatItem = flattenScopeRelations(dynamicStructure.item);
  const flatSection = Array.isArray(dynamicStructure.section)
    ? dynamicStructure.section
    : flattenScopeRelations(
        dynamicStructure.section as RelationScope | null | undefined,
      );

  if (
    flatItem === dynamicStructure.item &&
    flatSection === dynamicStructure.section
  ) {
    return dynamicStructure;
  }

  return {
    ...dynamicStructure,
    item: flatItem,
    section: flatSection,
  };
}
